import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { useI18n } from "../contexts/I18nContext";
import LanguagePicker from "../components/LanguagePicker";

export default function PrivacyPolicy() {
  const { locale } = useI18n();
  const isRu = locale === "ru";

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm font-bold text-[#02371b] hover:opacity-70 transition-opacity"
          >
            <ArrowLeft size={16} weight="bold" />
            {isRu ? "Назад к входу" : "Back to Login"}
          </Link>
          <LanguagePicker variant="light" />
        </div>

        <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm border border-neutral-200">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#41a42b]/10 text-[#41a42b] flex items-center justify-center">
              <ShieldCheck size={28} weight="fill" />
            </div>
            <h1 className="font-display text-3xl text-[#02371b]">
              {isRu ? "Политика конфиденциальности" : "Privacy Policy"}
            </h1>
          </div>

          <div className="prose prose-neutral max-w-none space-y-6 text-neutral-600">
            {isRu ? (
              <>
                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">1. Введение</h2>
                  <p>
                    TippTop («мы», «наш» или «нас») стремится защищать вашу конфиденциальность. Настоящая Политика конфиденциальности объясняет, как мы собираем, используем и передаем информацию о вас, когда вы используете наш веб-сайт и услуги.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">2. Сбор информации</h2>
                  <p>
                    Когда вы входите в систему с помощью провайдеров социального входа (Google, Яндекс, Mail.ru), мы собираем:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Ваше имя и URL-адрес изображения профиля.</li>
                    <li>Ваш адрес электронной почты.</li>
                    <li>Ваш уникальный идентификатор, предоставленный службой аутентификации.</li>
                  </ul>
                  <p className="mt-3">
                    Мы также собираем информацию, которую вы предоставляете напрямую, например, ваши прогнозы на турниры и названия групп.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">3. Использование информации</h2>
                  <p>
                    Мы используем собранную информацию для:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Предоставления и поддержки сервиса TippTop.</li>
                    <li>Персонализации вашего опыта (отображение вашего имени и аватара в списках лидеров).</li>
                    <li>Управления вашими прогнозами на турниры и членством в группах.</li>
                    <li>Связи с вами по поводу обновлений, связанных с обслуживанием.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">4. Передача данных</h2>
                  <p>
                    Мы не продаем вашу личную информацию. Ваше имя и изображение профиля будут видны другим участникам групп, к которым вы присоединяетесь, и в общедоступных списках лидеров внутри приложения.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">5. Хранение данных</h2>
                  <p>
                    Мы используем Supabase для хранения данных и аутентификации. Ваши данные хранятся надежно в соответствии со стандартными отраслевыми практиками.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">6. Ваши права</h2>
                  <p>
                    Вы имеете право на доступ, обновление или удаление вашей информации. Вы можете управлять настройками своего профиля в приложении или связаться с нами, если хотите полностью удалить свой аккаунт.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">7. Контакты</h2>
                  <p>
                    Если у вас есть какие-либо вопросы по поводу настоящей Политики конфиденциальности, пожалуйста, свяжитесь с нами по адресу support@tipp-top.com.
                  </p>
                </section>

                <div className="pt-8 border-t border-neutral-100 text-sm text-neutral-400">
                  Последнее обновление: 14 мая 2026 г.
                </div>
              </>
            ) : (
              <>
                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">1. Introduction</h2>
                  <p>
                    TippTop ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share information about you when you use our website and services.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">2. Information We Collect</h2>
                  <p>
                    When you sign in using social login providers (Google, Yandex, Mail.ru), we collect:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Your name and profile picture URL.</li>
                    <li>Your email address.</li>
                    <li>Your unique identifier provided by the authentication service.</li>
                  </ul>
                  <p className="mt-3">
                    We also collect information you provide directly, such as your tournament predictions and group names.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">3. How We Use Your Information</h2>
                  <p>
                    We use the collected information to:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Provide and maintain the TippTop service.</li>
                    <li>Personalize your experience (displaying your name and avatar in leaderboards).</li>
                    <li>Manage your tournament predictions and group memberships.</li>
                    <li>Communicate with you about service-related updates.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">4. Data Sharing</h2>
                  <p>
                    We do not sell your personal information. Your name and profile picture will be visible to other members of the groups you join and on public leaderboards within the application.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">5. Data Storage</h2>
                  <p>
                    We use Supabase for data storage and authentication. Your data is stored securely following industry-standard practices.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">6. Your Rights</h2>
                  <p>
                    You have the right to access, update, or delete your information. You can manage your profile settings within the app or contact us if you wish to delete your account entirely.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-bold text-[#02371b] mb-3">7. Contact Us</h2>
                  <p>
                    If you have any questions about this Privacy Policy, please contact us at support@tipp-top.com.
                  </p>
                </section>

                <div className="pt-8 border-t border-neutral-100 text-sm text-neutral-400">
                  Last updated: May 14, 2026
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
